'use client'

import React, { useRef, useCallback } from 'react'
import { useTranslations, useLocale } from 'next-intl'

import { getFlightLogo } from '@/src/utils/confirmation/airlineUtils'

import { DesktopTopAirlines } from './desktopTopAirlines'
import { Airline } from '@/src/api/requests/topAirlines'

interface DesktopTopAirlinesWrapperProps {
    popularAirlines?: Airline[]
}

export const DesktopTopAirlinesWrapper: React.FC<DesktopTopAirlinesWrapperProps> = ({ popularAirlines }) => {
    const t = useTranslations('homePage.headings')
    const locale = useLocale()
    const isRTL = locale === 'ar'
    const isArabic = locale === 'ar'
    const scrollContainerRef = useRef<HTMLDivElement>(null)

    const scroll = useCallback(
        (direction: 'left' | 'right') => {
            if (!scrollContainerRef.current) return

            const container = scrollContainerRef.current
            // Desktop: 150px card width + 16px gap (gap: 2 = 16px)
            const cardWidth = 150
            const gap = 16
            const scrollAmount = cardWidth + gap
            const scrollPosition = direction === 'left' ? -scrollAmount : scrollAmount

            container.scrollBy({
                left: isRTL ? -scrollPosition : scrollPosition,
                behavior: 'smooth'
            })
        },
        [isRTL]
    )

    const handleScrollLeft = useCallback(() => {
        scroll(isRTL ? 'right' : 'left')
    }, [scroll, isRTL])

    const handleScrollRight = useCallback(() => {
        scroll(isRTL ? 'left' : 'right')
    }, [scroll, isRTL])


    if (!popularAirlines || popularAirlines.length === 0) {
        return null
    }

    const processedAirlines = popularAirlines.map(airline => {
        const displayName = isArabic ? airline.name_ar || airline.name_en : airline.name_en
        const airlineCode = airline.id
        const upperCaseCode = airlineCode ? airlineCode.toUpperCase() : ''
        const logoUrl = upperCaseCode
            ? getFlightLogo(upperCaseCode)
            : 'https://ui.cltp.co/images/logos/air-logos/DEFAULT_2x.png'

        return {
            airline: {
                ...airline,
                name: displayName,
                href: isArabic ? airline.href_ar || airline.href_en : airline.href_en
            },
            airlineCode,
            logoUrl
        }
    })

    return (
        <DesktopTopAirlines
            title={t('topAirlines')}
            airlines={processedAirlines}
            isRTL={isRTL}
            onScrollLeft={handleScrollLeft}
            onScrollRight={handleScrollRight}
            scrollContainerRef={scrollContainerRef}
        />
    )
}

export default DesktopTopAirlinesWrapper